import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Key, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirmPw(""); setShowCurrent(false); setShowNext(false); };

  const submit = async (e) => {
    e?.preventDefault();
    if (!current || !next) { toast.error("All fields are required"); return; }
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirmPw) { toast.error("Passwords don't match"); return; }
    if (current === next) { toast.error("New password must differ from your current one"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed");
      reset();
      onOpenChange(false);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Change failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key size={18} weight="bold" /> Change password
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <PasswordField
            label="Current password"
            value={current}
            onChange={setCurrent}
            show={showCurrent}
            toggle={() => setShowCurrent((v) => !v)}
            dataTestId="cp-current"
          />
          <PasswordField
            label="New password"
            value={next}
            onChange={setNext}
            show={showNext}
            toggle={() => setShowNext((v) => !v)}
            dataTestId="cp-new"
          />
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Confirm new password</Label>
            <Input
              type={showNext ? "text" : "password"}
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              className="mt-1.5"
              data-testid="cp-confirm"
              autoComplete="new-password"
            />
          </div>
          <div className="text-[11px] text-zinc-500">
            Use at least 6 characters. After change, all future sessions on other devices will be invalidated on next request.
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="cp-submit">
              {saving ? "Saving…" : "Change password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PasswordField({ label, value, onChange, show, toggle, dataTestId }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{label}</Label>
      <div className="relative mt-1.5">
        <Input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
          data-testid={dataTestId}
        />
        <button
          type="button"
          onClick={toggle}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
          tabIndex={-1}
        >
          {show ? <EyeSlash size={15} /> : <Eye size={15} />}
        </button>
      </div>
    </div>
  );
}
